import { useState } from 'react';
import { Alert, Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { HMIComputedFormula, HMITemplate } from '../../types';
import { addComputedFormula, deleteComputedFormula, updateComputedFormula } from '../../layoutUtils';
import { describeComputedFormula } from '../../computedFields';
import ComputedFieldEditorDialog, { ComputedFormulaDraft } from './ComputedFieldEditorDialog';

export interface ComputedFieldsPanelProps {
  template: HMITemplate;
  readOnly?: boolean;
  onChange: (next: HMITemplate) => void;
}

type PendingFormula = { kind: 'add' } | { kind: 'edit'; formula: HMIComputedFormula } | null;

/**
 * Template-wide list of computed/derived field formulas (see
 * `HMIComputedFormula`) - fields whose value is calculated in the UI from
 * other fields (e.g. "Heating Time" = highest number of ON cells in any row
 * of a lamp-power grid, multiplied by "Time per Division"). These never come
 * from the recipe file or the device; they're re-evaluated locally whenever
 * a dependency changes.
 */
function ComputedFieldsPanel({ template, readOnly, onChange }: ComputedFieldsPanelProps) {
  const [pending, setPending] = useState<PendingFormula>(null);
  const { fields } = template;
  const formulas = template.computedFormulas ?? [];

  const handleSave = (draft: ComputedFormulaDraft) => {
    if (pending?.kind === 'add') {
      onChange(addComputedFormula(template, draft));
    } else if (pending?.kind === 'edit') {
      onChange(updateComputedFormula(template, pending.formula.id, draft));
    }
    setPending(null);
  };

  const handleDelete = (formulaId: string) => {
    onChange(deleteComputedFormula(template, formulaId));
    setPending(null);
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Define fields that are calculated in the UI instead of coming from the recipe file - for example a
        "Heating Time" that multiplies the busiest row of a lamp-power grid by a "Time per Division" field. The
        target field is recalculated automatically whenever any field it depends on changes.
      </Typography>

      {fields.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Add fields to a section first - a computed formula reads and writes field values.
        </Alert>
      )}

      <Stack spacing={1}>
        {formulas.map((formula) => (
          <Paper
            key={formula.id}
            variant="outlined"
            sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2">{describeComputedFormula(formula, fields)}</Typography>
            </Box>
            {!readOnly && (
              <Stack direction="row" spacing={0}>
                <Tooltip title="Edit computed field">
                  <IconButton size="small" onClick={() => setPending({ kind: 'edit', formula })}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete computed field">
                  <IconButton size="small" onClick={() => handleDelete(formula.id)}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Paper>
        ))}
        {formulas.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No computed fields yet.
          </Typography>
        )}
      </Stack>

      {!readOnly && (
        <Button
          sx={{ mt: 2 }}
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={fields.length === 0}
          onClick={() => setPending({ kind: 'add' })}
        >
          Add computed field
        </Button>
      )}

      {pending && (
        <ComputedFieldEditorDialog
          formula={pending.kind === 'edit' ? pending.formula : undefined}
          fields={fields}
          onCancel={() => setPending(null)}
          onSave={handleSave}
          onDelete={pending.kind === 'edit' ? () => handleDelete(pending.formula.id) : undefined}
        />
      )}
    </Box>
  );
}

export default ComputedFieldsPanel;
